import type { KioskType, ResolutionStatus } from '@gozoki/api-types'
import { useQuery } from '@tanstack/react-query'
import { DateTime } from 'luxon'
import { useMemo } from 'react'
import type { DateRangePreset } from '~/components/ui/date-range-picker'
import { api } from '~/lib/tuyau'
import { useMigratedRight } from '~/utils/rbac'
import { VISIO_REVIEWS_CONFIG_READ, VISIO_REVIEWS_LEGACY_RIGHTS } from './tabs'

export interface PreviousPeriodValue {
  previousStart: string
  previousEnd: string
}

/**
 * Builds the API date params from a picker range. `from: null` ⇒ unbounded
 * past, so `dateStart` is omitted (the API treats its absence as "no lower bound").
 */
export const toApiDateRange = (range: { from: Date | null; to: Date }) => ({
  ...(range.from && { dateStart: range.from.toISOString() }),
  dateEnd: range.to.toISOString(),
})

/**
 * Returns the comparison period for trend computation:
 * - today → yesterday, midnight up to the current time-of-day (same elapsed day slice)
 * - mtd → same day-of-month range in the previous month
 * - ytd → same range in the previous year
 * - unbounded past (from === null) → null (no trend displayed)
 * - all others (including manual ranges with preset === null) →
 *   equal-length period immediately preceding [from, to)
 */
export const getPreviousPeriodForPreset = (
  preset: DateRangePreset | null,
  range: { from: Date | null; to: Date },
): PreviousPeriodValue | null => {
  // Unbounded past has no meaningful preceding period.
  if (range.from === null) return null

  const start = DateTime.fromJSDate(range.from)
  const end = DateTime.fromJSDate(range.to)

  if (preset === 'today') {
    // Fair day-to-hour comparison: same elapsed slice of yesterday (midnight → current time-of-day).
    return {
      previousStart: start.minus({ days: 1 }).toISO()!,
      previousEnd: end.minus({ days: 1 }).toISO()!,
    }
  }

  if (preset === 'mtd') {
    const prevMonthStart = start.minus({ months: 1 }).startOf('month')
    // Map "today" within the current month to the same day in the previous
    // month, clamped to the previous month's last day (e.g. Mar 31 → Feb 28).
    const dayOffset = end.diff(start.startOf('month'), 'days').days
    const prevMonthEnd = DateTime.min(
      prevMonthStart.plus({ days: dayOffset }),
      prevMonthStart.endOf('month'),
    )
    return {
      previousStart: prevMonthStart.toISO()!,
      previousEnd: prevMonthEnd.endOf('day').toISO()!,
    }
  }

  if (preset === 'ytd') {
    const prevYearStart = start.minus({ years: 1 }).startOf('year')
    const prevYearEnd = end.minus({ years: 1 }).endOf('day')
    return { previousStart: prevYearStart.toISO()!, previousEnd: prevYearEnd.toISO()! }
  }

  // Default: equal-length preceding window
  const durationMs = end.toMillis() - start.toMillis()
  const previousEnd = start
  const previousStart = start.minus({ milliseconds: durationMs })
  return { previousStart: previousStart.toISO()!, previousEnd: previousEnd.toISO()! }
}

export interface GradeThresholds {
  low: number
  high: number
}

export const DEFAULT_THRESHOLDS: GradeThresholds = { low: 5, high: 8 }

export const MAX_GRADE = 10

/**
 * Les seuils vivent dans la configuration du module : la lecture d'un seul onglet suffit à les
 * obtenir. Sans aucun de ces droits on ne lance pas la requête (elle serait refusée) et on colore
 * les notes avec les seuils par défaut.
 */
export const VISIO_REVIEWS_STALE_TIME = 120_000

export const useGradeThresholds = (): GradeThresholds => {
  const canReadConfig = useMigratedRight(VISIO_REVIEWS_CONFIG_READ, VISIO_REVIEWS_LEGACY_RIGHTS)
  const { data: config } = useQuery(
    api.visioReviews.getConfig.queryOptions(
      {},
      { staleTime: VISIO_REVIEWS_STALE_TIME, enabled: canReadConfig },
    ),
  )
  return useMemo(
    () => ({
      low: config?.lowGradeThreshold ?? DEFAULT_THRESHOLDS.low,
      high: config?.highGradeThreshold ?? DEFAULT_THRESHOLDS.high,
    }),
    [config],
  )
}

/**
 * Picks the grade to display for an interaction.
 *
 * Manager grade is the source of truth: if a manager has reviewed the
 * interaction, that grade supersedes the AI grade. The AI grade is only
 * used as a fallback. Returns null when neither exists.
 *
 * Use this anywhere a single grade is displayed, filtered, or sorted —
 * never read `grade` directly when both fields are available.
 */
export const getEffectiveGrade = (item: {
  grade: number | null
  managerGrade: number | null
}): { value: number; isManager: boolean } | null => {
  if (item.managerGrade !== null) return { value: item.managerGrade, isManager: true }
  if (item.grade !== null) return { value: item.grade, isManager: false }
  return null
}

type RatingLevel = 'good' | 'mid' | 'bad'

export const getRatingLevel = (grade: number, thresholds: GradeThresholds): RatingLevel => {
  if (grade >= thresholds.high) return 'good'
  if (grade > thresholds.low) return 'mid'
  return 'bad'
}

// Earth-tone palette aligned with the back-office 2025 Figma:
// sage green (#6a9560), warm orange (#ef8031), muted red (#e03b3b).
const RATING_COLOR: Record<RatingLevel, string> = {
  good: 'text-[#6a9560]',
  mid: 'text-[#ef8031]',
  bad: 'text-[#e03b3b]',
}
const RATING_BG: Record<RatingLevel, string> = {
  good: 'bg-[#6a9560]',
  mid: 'bg-[#ef8031]',
  bad: 'bg-[#e03b3b]',
}

/** Hex equivalents of RATING_BG, for SVG-fill consumers (Recharts, raw bars). */
const RATING_FILL: Record<RatingLevel, string> = {
  good: '#6a9560',
  mid: '#ef8031',
  bad: '#e03b3b',
}

export const NO_GRADE_FILL = '#d8d4c8'
const RATING_BADGE: Record<RatingLevel, string> = {
  good: 'bg-[#e7efe1] text-[#3a5a35]',
  mid: 'bg-[#fad7ae] text-[#8a4a1a]',
  bad: 'bg-[#f8dada] text-[#8a2424]',
}
const RATING_CHIP_RING: Record<RatingLevel, string> = {
  good: 'ring-1 ring-[#6a9560]/30',
  mid: 'ring-1 ring-[#ef8031]/30',
  bad: 'ring-1 ring-[#e03b3b]/30',
}
const RATING_BUTTON: Record<RatingLevel, string> = {
  good: 'bg-[#6a9560] text-white',
  mid: 'bg-[#ef8031] text-white',
  bad: 'bg-[#e03b3b] text-white',
}

export const getRatingColor = (grade: number, thresholds: GradeThresholds) =>
  RATING_COLOR[getRatingLevel(grade, thresholds)]

export const getRatingBgColor = (grade: number, thresholds: GradeThresholds) =>
  RATING_BG[getRatingLevel(grade, thresholds)]

/**
 * Hex fill for SVG/Recharts consumers, keyed off the same thresholds as the
 * Tailwind bg colors so chart bars stay in lockstep with badge backgrounds.
 * Returns the neutral grey when the grade is null (e.g. ungraded interactions).
 */
export const gradeToFill = (grade: number | null, thresholds: GradeThresholds): string =>
  grade === null ? NO_GRADE_FILL : RATING_FILL[getRatingLevel(grade, thresholds)]

export const getRatingBadgeColor = (grade: number, thresholds: GradeThresholds) =>
  RATING_BADGE[getRatingLevel(grade, thresholds)]

export const getRatingChipRing = (grade: number, thresholds: GradeThresholds) =>
  RATING_CHIP_RING[getRatingLevel(grade, thresholds)]

export const getRatingButtonColor = (grade: number, thresholds: GradeThresholds) =>
  RATING_BUTTON[getRatingLevel(grade, thresholds)]

const RESOLUTION_STATUS_BADGE: Record<ResolutionStatus, string> = {
  OUI: 'bg-[#e7efe1] text-[#3a5a35]',
  NON: 'bg-[#f8dada] text-[#8a2424]',
  DOUTE: 'bg-[#fad7ae] text-[#8a4a1a]',
}

export const resolutionStatusBadgeClass = (status: ResolutionStatus): string =>
  RESOLUTION_STATUS_BADGE[status]

export const RESOLUTION_STATUS_ALL = 'all'

export const RESOLUTION_STATUS_OPTIONS = [
  { value: RESOLUTION_STATUS_ALL, label: 'Toutes' },
  { value: 'OUI', label: '🟢 Résolu' },
  { value: 'NON', label: '🔴 Non résolu' },
  { value: 'DOUTE', label: '🟠 Doute' },
]

export const resolutionStatusParam = (value: string): ResolutionStatus | undefined =>
  value === RESOLUTION_STATUS_ALL ? undefined : (value as ResolutionStatus)

export const getRankBadgeColor = (index: number) => {
  // Medal ladder for top 3 (gold / silver / bronze tinted with the Figma palette),
  // muted beige for the rest so the ranking stays visually quiet.
  if (index === 0) return 'bg-[#fad7ae] text-[#8a4a1a] ring-[#ef8031]/40'
  if (index === 1) return 'bg-[#ece9e1] text-[#535862] ring-[#c8c3b6]'
  if (index === 2) return 'bg-[#f1e2cf] text-[#8a4a1a] ring-[#d6b48a]'
  return 'bg-[#f4f2ee] text-[#535862] ring-[#e9eaeb]'
}

export const formatDurationSeconds = (seconds: number | null | undefined): string => {
  if (seconds === null || seconds === undefined) return 'N/A'
  if (seconds < 60) return `${seconds}s`
  if (seconds < 3600) {
    const minutes = Math.floor(seconds / 60)
    const remaining = seconds % 60
    return `${minutes}min ${remaining}s`
  }
  const hours = Math.floor(seconds / 3600)
  const minutes = Math.floor((seconds % 3600) / 60)
  return `${hours}h ${minutes}min`
}

export const formatFrDate = (iso: string): string => new Date(iso).toLocaleDateString('fr-FR')

export const formatFrTime = (iso: string): string =>
  new Date(iso).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })

export const formatFrDateTime = (iso: string): string =>
  `${formatFrDate(iso)} à ${formatFrTime(iso)}`

export const KIOSK_TYPE_ALL = 'all'

export const kioskTypeLabel = (type: string): string => {
  if (type === 'CHECK_IN') return 'Extérieur'
  if (type === 'CHECK_OUT') return 'Intérieur'
  return type
}

export const KIOSK_TYPE_OPTIONS = [
  { value: KIOSK_TYPE_ALL, label: 'Tous' },
  { value: 'CHECK_IN', label: kioskTypeLabel('CHECK_IN') },
  { value: 'CHECK_OUT', label: kioskTypeLabel('CHECK_OUT') },
]

/**
 * Converts the ternary kiosk-type filter state into the `kioskTypes` query
 * param the backend expects. `'all'` ⇒ undefined (no filter).
 */
export const kioskTypesParam = (value: string): string | undefined =>
  value === KIOSK_TYPE_ALL ? undefined : value

/**
 * Variante typée du filtre pour les endpoints Tuyau : leur schéma vine attend un
 * tableau d'enum, là où le client legacy sérialisait une chaîne.
 */
export const kioskTypesQuery = (value: string | undefined): KioskType[] | undefined =>
  value === 'CHECK_IN' || value === 'CHECK_OUT' ? [value] : undefined

/**
 * Inverse of `kioskTypesParam`: maps a query-param string back to the
 * ternary filter state used by `KIOSK_TYPE_OPTIONS`. Anything outside the
 * known set falls back to `'all'`.
 */
export const kioskTypeFromParam = (value: string | undefined): string =>
  value === 'CHECK_IN' || value === 'CHECK_OUT' ? value : KIOSK_TYPE_ALL

export const ALL_OPERATORS_VALUE = 'all-operators'
export const ALL_STORES_VALUE = 'all-stores'

export const useVisioReviewFilterSelectOptions = (filterOptions?: {
  operators?: { id: number; firstName: string; lastName: string }[]
  stores?: { id: number; label: string }[]
}) => {
  const operatorFilterOptions = useMemo(() => {
    const base = [{ value: ALL_OPERATORS_VALUE, label: 'Tous les opérateurs' }]
    if (!filterOptions?.operators) return base
    return [
      ...base,
      ...filterOptions.operators.map((u) => ({
        value: String(u.id),
        label: `${u.firstName} ${u.lastName}`,
      })),
    ]
  }, [filterOptions])

  const storeFilterOptions = useMemo(() => {
    const base = [{ value: ALL_STORES_VALUE, label: 'Toutes les boutiques' }]
    if (!filterOptions?.stores) return base
    return [...base, ...filterOptions.stores.map((s) => ({ value: String(s.id), label: s.label }))]
  }, [filterOptions])

  return { operatorFilterOptions, storeFilterOptions }
}
